/**
 * Pixel density measurement utilities for converting between CSS physical units
 * and pixels. Particularly useful for PDF export and print layouts.
 */

// Cache the measurement to avoid repeated DOM manipulation
let cachedPPI: number | null = null;

/**
 * Measures the pixel density (pixels per inch) by creating a temporary element
 * with height of '1in' and measuring its computed pixel height. The measurement
 * is cached after the first call for performance.
 *
 * NOTE: This value is typically 96 PPI on most displays, but can vary with
 * browser zoom and device settings.
 *
 * @returns The number of pixels per inch
 */
export function evaluatePixelDensity(): number {
  if (cachedPPI !== null) {
    return cachedPPI;
  }

  const element = document.createElement('div');
  element.style.position = 'absolute';
  element.style.visibility = 'hidden';
  element.style.height = '1in';
  element.style.width = '0';
  element.style.pointerEvents = 'none';
  element.style.top = '-1in'; // Off-screen positioning

  document.body.appendChild(element);

  const rect = element.getBoundingClientRect();
  const ppi = rect.height;

  document.body.removeChild(element);

  cachedPPI = ppi;
  return ppi;
}
